/*
 * Hook to handle guest wishlist merge on login for IAM redirect scenarios.
 * This hook is brand-specific and controlled by app.preferences.enableGuestWishlistMergeOnLogin config.
 */
import {useEffect} from 'react';

import {getConfig} from 'coreUtils/utils';
import {ECustomerAuthType} from 'coreClasses/customer/ICustomer';
import Customer from 'coreClasses/customer/Customer';
import type {CustomerProductListItem} from 'coreStore/data/customerProductLists/types';

import type {ICustomerProductList} from './useCustomerProductList';

interface GuestWishlistItem {
	productId: string;
	quantity: number;
	id: string;
	c_variationGroupId?: string;
}

interface GuestWishlistData {
	listId: string;
	customerId: string;
	timestamp: number;
	items: GuestWishlistItem[];
}

type Wishlist = Pick<ICustomerProductList, 'productListId' | 'items' | 'init'>;

interface UseGuestWishlistMergeProps {
	customer: Customer;
	wishlist: Wishlist;
}

const STORAGE_KEY = 'guestWishlistData';
const MAX_AGE = 5 * 60 * 1000; // 5 minutes

/**
 * Clear guest wishlist data from sessionStorage
 */
const clearGuestWishlistData = (): void => {
	sessionStorage.removeItem(STORAGE_KEY);
};

/**
 * Save guest wishlist data to sessionStorage
 * @param wishlistProductListId - The product list ID of the wishlist
 * @param wishlistItems - The items to save
 * @param customerId - The ID of the customer
 */
const saveGuestWishlistData = (wishlistProductListId: string, wishlistItems: CustomerProductListItem[], customerId: string): void => {
	const guestWishlistData: GuestWishlistData = {
		listId: wishlistProductListId,
		customerId: customerId,
		timestamp: Date.now(),
		items: wishlistItems.map(({productId, quantity, id, c_variationGroupId}) => ({
			productId,
			quantity,
			id,
			c_variationGroupId,
		})),
	};

	sessionStorage.setItem(STORAGE_KEY, JSON.stringify(guestWishlistData));
};

/**
 * Load and validate guest wishlist data from sessionStorage
 * Validates only timestamp (5 minutes max age)
 * Real security is handled server-side by Salesforce Commerce API
 */
const loadGuestWishlistData = (): GuestWishlistItem[] => {
	const savedData = sessionStorage.getItem(STORAGE_KEY);

	if (!savedData) {
		return [];
	}

	let data: GuestWishlistData;

	try {
		data = JSON.parse(savedData);
	} catch (e) {
		console.error('Failed to parse guest wishlist data:', e);
		clearGuestWishlistData();

		return [];
	}

	// Check if data is not too old (max 5 minutes)
	if (Date.now() - data.timestamp > MAX_AGE) {
		clearGuestWishlistData();

		return [];
	}

	return data.items;
};

/**
 * Custom hook to handle guest wishlist merge on login
 * Only active if enableGuestWishlistMergeOnLogin is true in brand config
 */
export const useGuestWishlistMerge = ({customer, wishlist}: UseGuestWishlistMergeProps) => {
	const {app} = getConfig();
	const isEnabled = app?.preferences?.enableGuestWishlistMergeOnLogin || false;

	useEffect(() => {
		if (!isEnabled) {
			return;
		}

		// Save guest wishlist data when user is guest
		if (customer.authType === ECustomerAuthType.GUEST && wishlist.productListId && wishlist.items?.length > 0) {
			saveGuestWishlistData(wishlist.productListId, wishlist.items, customer.customerId);
		}

		// Merge guest wishlist data when user becomes registered
		if (customer.isInitialized && customer.isRegistered) {
			const guestItems = loadGuestWishlistData();

			if (guestItems.length > 0) {
				// Merge guest items into registered user's wishlist

				try {
					wishlist.init(undefined, guestItems);
				} catch (e) {
					console.error('Failed to merge guest wishlist data:', e);
				}

				clearGuestWishlistData();
			}
		}
	}, [
		customer.authType,
		customer.customerId,
		customer.isInitialized,
		wishlist.productListId,
		wishlist.items,
		isEnabled,
	]);
};

export default useGuestWishlistMerge;
